import { useRef, useEffect, useCallback } from 'react';

/**
 * Хук для обробки показу реклами через AdsGram SDK
 */
type AdsgramController = ReturnType<NonNullable<typeof window.Adsgram>['init']>;

/**
 * How long to wait for the ad to APPEAR. This must never bound how long the
 * player then spends watching it: an earlier build timed the whole show and a
 * 5s limit cut a running ad off mid-view.
 */
export const AD_START_TIMEOUT_MS = 8_000;

/**
 * Safety net once the ad is on screen, so a stuck SDK cannot leave the button
 * disabled forever. Deliberately far longer than any interstitial.
 */
export const AD_WATCH_CAP_MS = 180_000;

/** @deprecated kept so existing imports keep resolving. */
export const AD_SHOW_TIMEOUT_MS = AD_START_TIMEOUT_MS;

/**
 * Two DIFFERENT AdsGram Rewarded Interstitial blocks, on purpose — they show
 * up as separate "revenue per view" lines in the dashboard, and mixing them
 * up (both call sites used to share one hardcoded blockId) makes that
 * reporting meaningless. Keep them distinct even if they're ever the same
 * value again.
 */
export const AD_QUEST_BLOCK_ID = 'int-26097'; // Daily Quests, quest 8 (interstitial)

export class AdsGramShowTimeoutError extends Error {
  constructor(timeoutMs = AD_START_TIMEOUT_MS) {
    super(`AdsGram did not start showing an ad within ${timeoutMs}ms`);
    this.name = 'AdsGramShowTimeoutError';
  }
}

class AdsGramShowError extends Error {
  constructor(reason: string) {
    super(`AdsGram failed: ${reason}`);
    this.name = 'AdsGramShowError';
  }
}

/**
 * AdsGram rejects with a bare object, not an Error, so String() on it produced
 * the useless "[object Object]". Pull out whatever the SDK actually said.
 */
export function describeAdError(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const bag = value as Record<string, unknown>;
    const named = ['description', 'message', 'error', 'reason', 'code']
      .map((key) => (typeof bag[key] === 'string' || typeof bag[key] === 'number' ? `${key}=${String(bag[key])}` : null))
      .filter(Boolean);
    if (named.length > 0) return named.join(' ');
    try {
      const json = JSON.stringify(value);
      if (json && json !== '{}') return json;
    } catch {
      // circular or otherwise unserialisable — fall through
    }
    const ctor = (value as { constructor?: { name?: string } }).constructor?.name;
    return ctor ? `unreadable ${ctor}` : 'unreadable object';
  }
  return String(value);
}

type PendingAdShow = {
  reject: (error: Error) => void;
};

type PendingAdShowRef = {
  current: PendingAdShow | null;
};

/**
 * The ONLY event names AdsGram accepts. It throws synchronously on anything
 * else — an earlier build guessed 'onShow' and got
 * "The event argument must be one of onReward, onStart, onSkip,
 *  onBannerNotFound, onError, onComplete, onNonStopShow, onTooLongSession".
 */
const ADSGRAM_EVENTS = [
  'onReward',
  'onStart',
  'onSkip',
  'onBannerNotFound',
  'onError',
  'onComplete',
  'onNonStopShow',
  'onTooLongSession',
] as const;

export type AdsgramEvent = (typeof ADSGRAM_EVENTS)[number];

export function isAdsgramEvent(name: string): name is AdsgramEvent {
  return (ADSGRAM_EVENTS as readonly string[]).includes(name);
}

/** Events proving the creative is actually on screen. */
const AD_STARTED_EVENTS: AdsgramEvent[] = ['onStart'];

/**
 * Watches for evidence that the ad really opened, so the start timeout can be
 * stood down. Any one signal is enough; if none arrives the ad never appeared.
 * Returns a disposer.
 */
function defaultObserveAdStart(controller: AdsgramController, onStarted: () => void): () => void {
  const disposers: Array<() => void> = [];

  if (controller.addEventListener && controller.removeEventListener) {
    for (const event of AD_STARTED_EVENTS) {
      // Guarded: the SDK throws on names it does not know, and a throw here
      // used to escape before cleanup ran, poisoning the next attempt with
      // "another show request is still pending".
      try {
        controller.addEventListener(event, onStarted);
        disposers.push(() => controller.removeEventListener?.(event, onStarted));
      } catch {
        // unsupported by this SDK build — other start signals still apply
      }
    }
  }

  if (typeof document !== 'undefined') {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onStarted();
    };
    document.addEventListener('visibilitychange', onVisibility);
    disposers.push(() => document.removeEventListener('visibilitychange', onVisibility));

    if (typeof MutationObserver !== 'undefined' && document.body) {
      const looksLikeAd = (node: Node): boolean => {
        if (!(node instanceof Element)) return false;
        if (node.tagName === 'IFRAME') return true;
        const id = node.id || '';
        const cls = typeof node.className === 'string' ? node.className : '';
        return /adsgram|adsgram-|sad-/i.test(`${id} ${cls}`);
      };
      const observer = new MutationObserver((records) => {
        for (const record of records) {
          for (const node of Array.from(record.addedNodes)) {
            if (looksLikeAd(node) || (node instanceof Element && node.querySelector?.('iframe'))) {
              onStarted();
              return;
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      disposers.push(() => observer.disconnect());
    }
  }

  return () => {
    for (const dispose of disposers) {
      try {
        dispose();
      } catch {
        // no-op
      }
    }
  };
}

export interface ShowAdOptions {
  startTimeoutMs?: number;
  watchCapMs?: number;
  /** Injectable for tests; defaults to SDK events + DOM/visibility detection. */
  observeAdStart?: (controller: AdsgramController, onStarted: () => void) => () => void;
}

export async function showAdsGramAdWithTimeout(
  controller: AdsgramController,
  onReward: () => void,
  pendingAdShowRef: PendingAdShowRef,
  options: ShowAdOptions = {},
): Promise<void> {
  const startTimeoutMs = options.startTimeoutMs ?? AD_START_TIMEOUT_MS;
  const watchCapMs = options.watchCapMs ?? AD_WATCH_CAP_MS;
  const observeAdStart = options.observeAdStart ?? defaultObserveAdStart;
  if (pendingAdShowRef.current) {
    throw new AdsGramShowError('another show request is still pending');
  }

  let rewardDelivered = false;
  let onRewardCallback: (() => void) | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let stopObservingStart: (() => void) | null = null;

  const cleanup = (pendingShow: PendingAdShow) => {
    clearTimeout(timeoutId);
    if (stopObservingStart) {
      stopObservingStart();
      stopObservingStart = null;
    }
    if (onRewardCallback && controller.removeEventListener) {
      controller.removeEventListener('onReward', onRewardCallback);
    }
    onRewardCallback = null;
    if (pendingAdShowRef.current === pendingShow) {
      pendingAdShowRef.current = null;
    }
  };

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const pendingShow: PendingAdShow = {
      reject: (error) => {
        if (settled) return;
        settled = true;
        cleanup(pendingShow);
        reject(error);
      },
    };

    const resolveWithReward = () => {
      if (settled) return;
      try {
        if (!rewardDelivered) {
          rewardDelivered = true;
          onReward();
        }
      } catch (error: unknown) {
        pendingShow.reject(error instanceof Error ? error : new Error(String(error)));
        return;
      }

      settled = true;
      cleanup(pendingShow);
      resolve();
    };

    pendingAdShowRef.current = pendingShow;

    // Phase 1 — waiting for the ad to appear. Phase 2 starts the moment it
    // does, and only caps the session so the button cannot stick forever;
    // the player's viewing time is never what gets timed out.
    let adStarted = false;
    timeoutId = setTimeout(() => {
      pendingShow.reject(new AdsGramShowTimeoutError(startTimeoutMs));
    }, startTimeoutMs);

    const markAdStarted = () => {
      if (adStarted || settled) return;
      adStarted = true;
      clearTimeout(timeoutId);
      if (stopObservingStart) {
        stopObservingStart();
        stopObservingStart = null;
      }
      timeoutId = setTimeout(() => {
        pendingShow.reject(new AdsGramShowError(`ad never finished within ${watchCapMs}ms`));
      }, watchCapMs);
    };

    try {
      stopObservingStart = observeAdStart(controller, markAdStarted);
    } catch (error: unknown) {
      // Never leave pendingAdShowRef claimed: that is what produced the
      // follow-up "another show request is still pending" on the next tap.
      pendingShow.reject(error instanceof Error ? error : new AdsGramShowError(describeAdError(error)));
      return;
    }

    if (controller.addEventListener) {
      try {
        onRewardCallback = resolveWithReward;
        controller.addEventListener('onReward', onRewardCallback);
      } catch (error: unknown) {
        // Any throw during setup must be routed through pendingShow.reject so
        // cleanup runs; escaping the executor left the slot claimed and the
        // next tap reported "another show request is still pending".
        onRewardCallback = null;
        pendingShow.reject(error instanceof Error ? error : new AdsGramShowError(describeAdError(error)));
        return;
      }
    }

    void controller.show().then((result) => {
      if (result?.error === true) {
        pendingShow.reject(new AdsGramShowError(result.description || 'show() returned an error'));
        return;
      }
      resolveWithReward();
    }).catch((error: unknown) => {
      pendingShow.reject(error instanceof Error ? error : new AdsGramShowError(describeAdError(error)));
    });
  });
}

/**
 * @param blockId - AdsGram Rewarded Interstitial block ID. Distinct blocks exist
 * per surface (see the dashboard's "revenue per view" labels), so this must be
 * passed explicitly rather than hardcoded — quest 8 and the onboarding ad
 * boost are DIFFERENT blocks and must not silently share one.
 */
export function useAdQuestHandler(blockId: string) {
  const adControllerRef = useRef<AdsgramController | null>(null);
  const pendingAdShowRef = useRef<PendingAdShow | null>(null);

  const rejectPendingAdShow = useCallback((reason: string) => {
    pendingAdShowRef.current?.reject(new AdsGramShowError(reason));
  }, []);

  const initAdsGram = useCallback(() => {
    if (typeof window === 'undefined' || !window.Adsgram) {
      return;
    }

    try {
      adControllerRef.current = window.Adsgram.init({
        blockId,
        onBannerNotFound: () => {
          rejectPendingAdShow('banner not found');
        },
        onNonStopShow: () => {
          rejectPendingAdShow('another ad is already being shown');
        },
        onTooLongSession: () => {
          rejectPendingAdShow('session is too long');
        },
        onError: () => {
          rejectPendingAdShow('SDK error callback');
        },
      });
    } catch (error: unknown) {
      console.error("[useAdQuestHandler] ✗ Failed to initialize AdsGram:", error);
    }
  }, [blockId, rejectPendingAdShow]);

  // Ініціалізуємо AdsGram при монтуванні компонента
  useEffect(() => {
    initAdsGram();

    return () => {
      rejectPendingAdShow('component unmounted');
      if (adControllerRef.current?.destroy) {
        adControllerRef.current.destroy();
      }
      adControllerRef.current = null;
    };
  }, [initAdsGram, rejectPendingAdShow]);

  /**
   * Показує рекламу через AdsGram
   * Якщо помилка - просто викидає помилку, щоб DailyQuestsModal міг обробити її
   */
  const showAdFromAdsGram = async (onReward: () => void): Promise<void> => {
    const controller = adControllerRef.current;
    if (!controller) {
      throw new Error("AdsGram is not available");
    }

    try {
      await showAdsGramAdWithTimeout(controller, onReward, pendingAdShowRef);
    } catch (error: unknown) {
      // A failed show leaves the SDK believing an ad is still in progress, so
      // every later attempt rejects within milliseconds (observed in prod:
      // one 5s timeout, then three instant failures in a row). Rebuild the
      // controller so the next tap starts from a clean one.
      try {
        adControllerRef.current?.destroy?.();
      } catch {
        // no-op
      }
      adControllerRef.current = null;
      // Belt and braces: whatever went wrong, the next tap must not inherit a
      // claimed slot.
      pendingAdShowRef.current = null;
      initAdsGram();
      throw error;
    }
  };

  const closeAd = (): boolean => {
    const controller = adControllerRef.current;
    if (!controller) {
      return false;
    }

    try {
      if (controller.close) {
        rejectPendingAdShow('ad was closed');
        controller.close();
        return true;
      }

      if (controller.hide) {
        rejectPendingAdShow('ad was hidden');
        controller.hide();
        return true;
      }

      if (controller.destroy) {
        rejectPendingAdShow('ad controller was destroyed');
        controller.destroy();
        adControllerRef.current = null;
        initAdsGram();
        return true;
      }
    } catch (error: unknown) {
      console.error("[useAdQuestHandler] ✗ Failed to close AdsGram ad:", error);
    }

    return false;
  };

  return {
    adControllerRef,
    showAdFromAdsGram,
    closeAd,
  };
}
